import { investorApi } from '@core/api/httpClient'
import { logger } from '@core/logging/logger'
import { formatCalendarDate, readFileNameFromContentDisposition } from '@shared/utils'
import { TRANSACTION_ENDPOINTS } from './transactionEndpointPaths'
import { buildTransactionQueryParams } from './transactionQueryParams'
import {
  parseRentalPayoutRow,
  parseTransactionDetail,
  parseTransactionsResponse,
  parseTransactionsFilters,
  type ParsedTransactionsPage,
  type TransactionsFiltersDto,
} from './transactionParser'
import type { TransactionApiRow, TransactionsApiResponseDto } from './transactionContracts'
import type { TransactionDetail } from '../types/transactionDetailTypes'
import type {
  RentalPayout,
  RentalPayoutsRange,
  TransactionFilters,
  TransactionStatement,
} from '../types/transactionTypes'

export interface FetchTransactionsParams {
  page: number
  limit: number
  filters: TransactionFilters
}

export async function getTransactions(
  params: FetchTransactionsParams,
): Promise<ParsedTransactionsPage> {
  const query = {
    ...buildTransactionQueryParams(params.filters),
    page: params.page,
    limit: params.limit,
  }

  const { data } = await investorApi.get<unknown>(TRANSACTION_ENDPOINTS.LIST, { params: query })
  return parseTransactionsResponse(data)
}

export async function getTransactionDetail(id: string): Promise<TransactionDetail> {
  const { data } = await investorApi.get<unknown>(TRANSACTION_ENDPOINTS.DETAIL(id))
  return parseTransactionDetail(data)
}

export async function getTransactionsFilters(): Promise<TransactionsFiltersDto> {
  const { data } = await investorApi.get<unknown>(TRANSACTION_ENDPOINTS.FILTERS)
  return parseTransactionsFilters(data)
}

/** Used when the server's name can't be read: `Sliceal_Transaction_Statement_YYYYMMDD.pdf`. */
const fallbackStatementFileName = (): string =>
  `Sliceal_Transaction_Statement_${formatCalendarDate().replace(/-/g, '')}.pdf`

/**
 * PDF statement of every row matching the filters — the list endpoint with
 * `isExport=true` and no page/limit. The server renders a fresh PDF per call,
 * so this only runs on an explicit user action, never as a prefetch.
 */
export async function exportTransactionsStatement(
  filters: TransactionFilters,
): Promise<TransactionStatement> {
  const response = await investorApi.get<Blob>(TRANSACTION_ENDPOINTS.LIST, {
    params: { ...buildTransactionQueryParams(filters), isExport: true },
    responseType: 'blob',
  })

  const fileName = readFileNameFromContentDisposition(response.headers['content-disposition'])
  if (!fileName) {
    // Either the server sent no name, or CORS hides the header from JS — it
    // must be listed in Access-Control-Expose-Headers. Flag it, don't mask it.
    logger.warn(
      'Statement export: Content-Disposition not readable; check the backend exposes it via Access-Control-Expose-Headers. Using fallback file name.',
    )
  }

  return { blob: response.data, fileName: fileName ?? fallbackStatementFileName() }
}

const RENTAL_PAYOUTS_PAGE_LIMIT = 100
/** Guard against a pagination bug turning this into an unbounded loop. */
const RENTAL_PAYOUTS_MAX_PAGES = 20

/**
 * Every rental payout credited in a date range. Pages through the activity
 * feed until it is exhausted — the endpoint returns no per-period aggregate,
 * so totals are only right once every row for the range is in hand.
 */
export async function getRentalPayouts(range: RentalPayoutsRange): Promise<RentalPayout[]> {
  const rows: TransactionApiRow[] = []
  for (let page = 1; page <= RENTAL_PAYOUTS_MAX_PAGES; page++) {
    const { data } = await investorApi.get<TransactionsApiResponseDto>(TRANSACTION_ENDPOINTS.LIST, {
      params: {
        type: 'rental_income',
        sort: 'newest_first',
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
        page,
        limit: RENTAL_PAYOUTS_PAGE_LIMIT,
      },
    })
    rows.push(...(data?.data ?? []))
    if (page >= (data?.pagination?.totalPages ?? 0)) break
  }
  return rows.map(parseRentalPayoutRow)
}
